/* mile — product page JS (vanilla, no dependencies, loaded with defer)
   Everything here is progressive enhancement: with JS off the page still has a
   working gallery, size picker, size-chart modal and add-to-cart.

   1. <mile-sticky-atc> — mobile sticky add-to-cart bar. Appears after the buy
      buttons scroll out of view. Submits the main product form (button[form]),
      so Dawn's product-form.js handles the AJAX add + cart drawer as usual.
      Label, disabled state, price and selected size stay in sync with the main
      form via MutationObservers.
   2. <mile-omnibus> — toggles the Omnibus lowest-price line depending on
      whether the currently selected variant is discounted (.price--on-sale).
   3. <mile-wishlist> — save without an account, localStorage only.
   4. DOM enhancements re-applied on every Dawn re-render (Dawn replaces the
      whole <variant-selects> subtree on variant change, so these must be
      idempotent and re-runnable):
      - size pills split "98[2-3r]" into a big size + a small age line
      - the size-chart trigger is lifted next to the "Veľkosť" legend
      - the mobile gallery gets dot navigation instead of a 1/5 counter
   5. [data-mile-scroll-target] — smooth scroll helper (stars -> reviews,
      sticky size chip -> variant picker). */

(() => {
  'use strict';

  /* Rozsah, v ktorom sa smie zobraziť sticky lišta s košíkom.
     Pôvodne len mobil (max-width: 749px). Na počítači ju potrebujeme tiež:
     informačný stĺpec je vysoký cez 1800 px, takže hlavné tlačidlo "Do košíka"
     odscrolluje asi po 650 px a späť sa už nedostane — sticky buy box v takej
     výške nepomôže. Šírku teraz neobmedzujeme, viditeľnosť riadi CSS. */
  const STICKY_ATC_MEDIA = '(min-width: 0px)';
  const WISHLIST_KEY = 'mile:wishlist';
  const SIZE_WITH_AGE = /^(.+?)\s*[[(]\s*([^\])]+?)\s*[\])]\s*$/;

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  /** Write only on change. The sticky bar lives inside <product-info>, which is
      watched by a MutationObserver — assigning identical text would replace the
      text node, fire the observer and loop forever. */
  const setText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };

  const sizeOptionNames = (root) =>
    (root.dataset.mileSizeOptions || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);

  /** Legend/label of the option group that holds sizes (or null). */
  function findSizeLabel(root, names) {
    const groups = root.querySelectorAll('.product-form__input');
    for (const group of groups) {
      const label = group.querySelector('legend.form__label, label.form__label');
      if (!label) continue;
      const text = label.textContent.trim().toLowerCase();
      if (names.some((name) => text.startsWith(name))) return label;
    }
    return null;
  }

  /* ----------------------------------------------------------------------
     1. Size pills: "98[2-3r]" -> 98 / 2-3r
     ---------------------------------------------------------------------- */

  function enhanceSizePills(root) {
    const labels = root.querySelectorAll(".product-form__input--pill input[type='radio'] + label");
    labels.forEach((label) => {
      if (label.dataset.mileSize) return;
      label.dataset.mileSize = 'done';

      const textNode = Array.from(label.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
      );
      if (!textNode) return;

      const match = textNode.textContent.trim().match(SIZE_WITH_AGE);
      if (!match) return;

      const fragment = document.createDocumentFragment();

      const value = document.createElement('span');
      value.className = 'mile-size__value';
      value.textContent = match[1].trim();

      const age = document.createElement('span');
      age.className = 'mile-size__age';
      age.textContent = match[2].trim();

      fragment.appendChild(value);
      fragment.appendChild(age);
      label.replaceChild(fragment, textNode);
      label.classList.add('mile-size--with-age');
    });
  }

  /* ----------------------------------------------------------------------
     2. Size-chart trigger next to the "Veľkosť" legend
        The original block stays in the DOM as a no-JS fallback; we only clone
        its behaviour upwards, because Dawn destroys anything we move into
        <variant-selects> on the next variant change.
     ---------------------------------------------------------------------- */

  function placeSizeChartLink(root, names) {
    const opener = root.querySelector('.mile-size-chart[data-modal]');
    if (!opener) return;

    const label = findSizeLabel(root, names);

    // Self-correcting: the standalone trigger is only hidden while a link
    // actually exists in the legend. If Dawn re-renders the picker and the
    // enhancement has not been re-applied yet, the original button comes back
    // rather than leaving the size guide unreachable.
    if (!label) {
      opener.classList.remove('mile-size-chart--relocated');
      return;
    }
    if (label.querySelector('.mile-size-chart__link')) {
      opener.classList.add('mile-size-chart--relocated');
      return;
    }

    const modalSelector = opener.dataset.modal;
    const text = opener.querySelector('.mile-size-chart__button-label');

    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'mile-size-chart__link';
    link.setAttribute('aria-haspopup', 'dialog');
    link.textContent = text ? text.textContent.trim() : '';
    link.addEventListener('click', () => {
      const modal = document.querySelector(modalSelector);
      if (modal && typeof modal.show === 'function') modal.show(link);
    });

    label.appendChild(link);
    label.classList.add('mile-size-label');
    opener.classList.add('mile-size-chart--relocated');
  }

  /* ----------------------------------------------------------------------
     3. Mobile gallery: dot navigation
        Dawn's arrows + "1 / 5" counter stay in the DOM (keyboard and screen
        readers keep using them, and their labels stay translatable); on mobile
        they are visually hidden and these dots take over as the touch
        affordance, which is why the dots are aria-hidden.
     ---------------------------------------------------------------------- */

  function buildGalleryDots(root, sectionId) {
    const sliderComponent = root.querySelector(`#GalleryViewer-${CSS.escape(sectionId)}`);
    if (!sliderComponent) return;

    const slider = sliderComponent.querySelector('[id^="Slider-Gallery-"]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('.product__media-item'));
    let nav = sliderComponent.querySelector('.mile-gallery-dots');

    if (slides.length < 2) {
      if (nav) nav.remove();
      return;
    }
    if (nav && nav.children.length === slides.length) return;
    if (nav) nav.remove();

    nav = document.createElement('div');
    nav.className = slides.length > 8 ? 'mile-gallery-dots mile-gallery-dots--many' : 'mile-gallery-dots';
    nav.setAttribute('aria-hidden', 'true');

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mile-gallery-dot';
      dot.tabIndex = -1;
      dot.addEventListener('click', () => {
        slider.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
      if (index === 0) dot.classList.add('is-active');
      nav.appendChild(dot);
    });

    sliderComponent.appendChild(nav);

    const setActive = (page) => {
      const index = Math.min(Math.max(page - 1, 0), nav.children.length - 1);
      Array.from(nav.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    if (!sliderComponent.dataset.mileDots) {
      sliderComponent.dataset.mileDots = 'done';
      sliderComponent.addEventListener('slideChanged', (event) => setActive(event.detail.currentPage));
    }

    const current = sliderComponent.querySelector('.slider-counter--current');
    setActive(current ? parseInt(current.textContent, 10) || 1 : 1);
  }

  /* ----------------------------------------------------------------------
     4. Enhancement runner — idempotent, re-run after every Dawn re-render
     ---------------------------------------------------------------------- */

  function enhance(root) {
    const names = sizeOptionNames(root);
    enhanceSizePills(root);
    if (names.length) placeSizeChartLink(root, names);
    if (root.dataset.section) buildGalleryDots(root, root.dataset.section);
    root.dispatchEvent(new CustomEvent('mile:enhanced', { bubbles: true }));
  }

  function initProductInfo(root) {
    if (root.dataset.mileEnhanced) return;
    root.dataset.mileEnhanced = 'done';

    enhance(root);

    // setTimeout, not requestAnimationFrame: rAF is suspended in background
    // tabs, and Dawn's variant re-render can land while the tab is hidden.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        enhance(root);
      }, 0);
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  const initAll = () => document.querySelectorAll('product-info[data-section]').forEach(initProductInfo);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);

  /* ----------------------------------------------------------------------
     5. Smooth scroll helper
     ---------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mile-scroll-target]');
    if (!trigger) return;

    const target = document.getElementById(trigger.dataset.mileScrollTarget);
    if (!target) return;

    event.preventDefault();
    const block = trigger.dataset.mileScrollBlock || 'start';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block });

    if (target.hasAttribute('tabindex')) {
      target.focus({ preventScroll: true });
    } else {
      target.classList.add('mile-flash');
      setTimeout(() => target.classList.remove('mile-flash'), 1200);
    }
  });

  /* ----------------------------------------------------------------------
     6. Sticky add-to-cart bar
     ---------------------------------------------------------------------- */

  if (!customElements.get('mile-sticky-atc')) {
    customElements.define(
      'mile-sticky-atc',
      class MileStickyAtc extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
          this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.priceSource = document.getElementById(`price-${this.sectionId}`);
          this.stickyButton = this.querySelector('[data-mile-sticky-button]');
          this.stickyLabel = this.querySelector('[data-mile-sticky-label]');
          this.stickyPrice = this.querySelector('[data-mile-sticky-price]');
          this.sizeChip = this.querySelector('[data-mile-sticky-size]');
          this.sizeChipLabel = this.querySelector('[data-mile-sticky-size-label]');

          if (!this.mainButton || !this.stickyButton) return;

          this.mediaQuery = window.matchMedia(STICKY_ATC_MEDIA);
          this.onMediaChange = () => {
            if (!this.mediaQuery.matches) this.classList.remove('is-visible');
          };
          this.mediaQuery.addEventListener('change', this.onMediaChange);

          /* Lišta má dve nezávislé podmienky, preto sa stav drží v dvoch
             premenných a vyhodnocuje na jednom mieste. */
          this.pastAtc = false;
          this.footerInView = false;

          this.syncVisibility = () => {
            const show = this.pastAtc && !this.footerInView && this.mediaQuery.matches;
            this.classList.toggle('is-visible', show);
          };

          const anchor = this.mainButton.closest('.product-form__buttons') || this.mainButton;
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.pastAtc = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
              });
              this.syncVisibility();
            },
            { threshold: 0 }
          );
          this.intersectionObserver.observe(anchor);

          /* Lišta je position: fixed, takže na konci stránky prekrývala pätičku —
             kontakt, právne odkazy aj EÚ odznak zmizli pod ňou. Keď sa pätička
             objaví, návštevník už produkt neprezerá, takže lištu schováme.
             Riešime to takto a nie odsadením zdola, aby nevznikla prázdna medzera
             vo chvíli, keď lišta zobrazená nie je. */
          const footer = document.querySelector('.mile-footer') || document.querySelector('footer');
          if (footer) {
            this.footerObserver = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  this.footerInView = entry.isIntersecting;
                });
                this.syncVisibility();
              },
              { threshold: 0 }
            );
            this.footerObserver.observe(footer);
          }

          this.buttonObserver = new MutationObserver(() => this.syncButton());
          this.buttonObserver.observe(this.mainButton, {
            attributes: true,
            attributeFilter: ['disabled'],
            childList: true,
            subtree: true,
            characterData: true,
          });
          this.syncButton();

          if (this.priceSource && this.stickyPrice) {
            this.priceObserver = new MutationObserver(() => this.syncPrice());
            this.priceObserver.observe(this.priceSource, { childList: true, subtree: true });
          }

          if (this.productInfo && this.sizeChip) {
            this.onEnhanced = () => this.syncSize();
            this.productInfo.addEventListener('mile:enhanced', this.onEnhanced);
            this.productInfo.addEventListener('change', this.onEnhanced);
            this.syncSize();
          }
        }

        disconnectedCallback() {
          this.intersectionObserver?.disconnect();
          this.footerObserver?.disconnect();
          this.buttonObserver?.disconnect();
          this.priceObserver?.disconnect();
          this.mediaQuery?.removeEventListener('change', this.onMediaChange);
          if (this.productInfo && this.onEnhanced) {
            this.productInfo.removeEventListener('mile:enhanced', this.onEnhanced);
            this.productInfo.removeEventListener('change', this.onEnhanced);
          }
        }

        syncButton() {
          const labelSpan = this.mainButton.querySelector('span');
          if (labelSpan && this.stickyLabel) {
            const text = labelSpan.textContent.trim();
            if (text) setText(this.stickyLabel, text);
          }
          this.stickyButton.toggleAttribute('disabled', this.mainButton.hasAttribute('disabled'));
        }

        syncPrice() {
          const priceElement = this.priceSource.querySelector('.price');
          if (!priceElement) return;
          const markup = priceElement.outerHTML;
          if (this.stickyPrice.innerHTML !== markup) this.stickyPrice.innerHTML = markup;
        }

        /** Mirror "Veľkosť: 98" from the picker. Label text comes from the
            legend, so it stays translatable without any string in JS. */
        syncSize() {
          const names = sizeOptionNames(this.productInfo);
          const label = names.length ? findSizeLabel(this.productInfo, names) : null;
          if (!label) {
            this.sizeChip.hidden = true;
            return;
          }

          const group = label.closest('.product-form__input');
          let value = '';

          const checked = group.querySelector("input[type='radio']:checked");
          const select = group.querySelector('select');
          if (checked) {
            const shortValue = checked.nextElementSibling?.querySelector('.mile-size__value');
            value = (shortValue ? shortValue.textContent : checked.value).trim();
          } else if (select && select.selectedOptions.length) {
            value = select.selectedOptions[0].value.trim();
          }

          // Fallback for the raw "98[2-3r]" form: the chip has no room for the age.
          const stripped = value.match(SIZE_WITH_AGE);
          if (stripped) value = stripped[1].trim();

          if (!value) {
            this.sizeChip.hidden = true;
            return;
          }

          const name = label.childNodes[0]?.textContent.trim().replace(/:$/, '') || '';
          setText(this.sizeChipLabel, name ? `${name}: ${value}` : value);
          this.sizeChip.hidden = false;
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     7. Omnibus line visibility
     ---------------------------------------------------------------------- */

  if (!customElements.get('mile-omnibus')) {
    customElements.define(
      'mile-omnibus',
      class MileOmnibus extends HTMLElement {
        connectedCallback() {
          this.priceSource = document.getElementById(`price-${this.dataset.sectionId}`);
          if (!this.priceSource) return;
          this.priceObserver = new MutationObserver(() => this.sync());
          this.priceObserver.observe(this.priceSource, { childList: true, subtree: true });
          this.sync();
        }

        disconnectedCallback() {
          this.priceObserver?.disconnect();
        }

        sync() {
          const onSale = Boolean(this.priceSource.querySelector('.price--on-sale'));
          this.toggleAttribute('hidden', !onSale);
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     8. Wishlist — localStorage, no account, no backend
     ---------------------------------------------------------------------- */

  function readWishlist() {
    try {
      const parsed = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      return true;
    } catch (error) {
      // private mode / storage full — the button simply stays unsaved
      return false;
    }
  }

  if (!customElements.get('mile-wishlist')) {
    customElements.define(
      'mile-wishlist',
      class MileWishlist extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.handle;
          this.button = this.querySelector('[data-mile-wishlist-button]');
          this.label = this.querySelector('[data-mile-wishlist-label]');
          if (!this.handle || !this.button) return;

          this.onClick = () => this.toggle();
          this.button.addEventListener('click', this.onClick);

          this.onStorage = (event) => {
            if (event.key === WISHLIST_KEY) this.render();
          };
          window.addEventListener('storage', this.onStorage);

          this.render();
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.onClick);
          window.removeEventListener('storage', this.onStorage);
        }

        get saved() {
          return readWishlist().includes(this.handle);
        }

        toggle() {
          const list = readWishlist();
          const index = list.indexOf(this.handle);
          if (index === -1) {
            list.push(this.handle);
          } else {
            list.splice(index, 1);
          }
          if (!writeWishlist(list)) return;
          this.render();
          document.dispatchEvent(
            new CustomEvent('mile:wishlist:change', { detail: { handle: this.handle, list } })
          );
        }

        render() {
          const saved = this.saved;
          this.classList.toggle('is-saved', saved);
          this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          if (this.label) {
            const text = saved ? this.button.dataset.labelAdded : this.button.dataset.labelAdd;
            if (text) setText(this.label, text);
          }
        }
      }
    );
  }
})();
